import CryptoJS from 'crypto-js';

const STORAGE_KEYS = {
  USERS: 'retailflow_users',
  PRODUCTS: 'retailflow_products',
  CATEGORIES: 'retailflow_categories',
  SUPPLIERS: 'retailflow_suppliers',
  TRANSACTIONS: 'retailflow_transactions',
  STOCK_HISTORY: 'retailflow_stock_history',
  SETTINGS: 'retailflow_settings',
  CURRENT_USER: 'retailflow_current_user',
  SESSION: 'retailflow_session'
};

class StorageManager {
  getItem(key) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`Error reading ${key}:`, error);
      return null;
    }
  }

  setItem(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error(`Error writing ${key}:`, error);
      return false;
    }
  }

  removeItem(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing ${key}:`, error);
      return false;
    }
  }

  clear() {
    localStorage.clear();
  }

  // Specific getters
  getUsers() {
    return this.getItem(STORAGE_KEYS.USERS) || [];
  }

  getProducts() {
    return this.getItem(STORAGE_KEYS.PRODUCTS) || [];
  }

  // Return products visible to a specific user (owners/managers see all)
  getProductsForUser(user) {
    const all = this.getProducts();
    if (!user) return [];
    // Frontend-only multi-tenant isolation: each user only sees their own products
    return all.filter(p => p.createdBy === user.id);
  }

  getCategories() {
    return this.getItem(STORAGE_KEYS.CATEGORIES) || [];
  }

  getSuppliers() {
    return this.getItem(STORAGE_KEYS.SUPPLIERS) || [];
  }

  // Suppliers scoped to a specific user
  getSuppliersForUser(user) {
    const all = this.getSuppliers();
    if (!user) return [];
    return all.filter(s => s.createdBy === user.id);
  }

  getTransactions() {
    return this.getItem(STORAGE_KEYS.TRANSACTIONS) || [];
  }

  getTransactionsForUser(user) {
    const all = this.getTransactions();
    if (!user) return [];
    // Frontend-only multi-tenant isolation: each user only sees their own transactions
    return all.filter(t => t.userId === user.id);
  }

  getStockHistory() {
    return this.getItem(STORAGE_KEYS.STOCK_HISTORY) || [];
  }

  getSettings() {
    return this.getItem(STORAGE_KEYS.SETTINGS) || this.getDefaultSettings();
  }

  getCurrentUser() {
    return this.getItem(STORAGE_KEYS.CURRENT_USER);
  }

  getSession() {
    return this.getItem(STORAGE_KEYS.SESSION);
  }

  // Specific setters
  setUsers(users) {
    return this.setItem(STORAGE_KEYS.USERS, users);
  }

  setProducts(products) {
    return this.setItem(STORAGE_KEYS.PRODUCTS, products);
  }

  setCategories(categories) {
    return this.setItem(STORAGE_KEYS.CATEGORIES, categories);
  }

  setSuppliers(suppliers) {
    return this.setItem(STORAGE_KEYS.SUPPLIERS, suppliers);
  }

  setTransactions(transactions) {
    return this.setItem(STORAGE_KEYS.TRANSACTIONS, transactions);
  }

  setStockHistory(history) {
    return this.setItem(STORAGE_KEYS.STOCK_HISTORY, history);
  }

  setSettings(settings) {
    return this.setItem(STORAGE_KEYS.SETTINGS, settings);
  }

  setCurrentUser(user) {
    return this.setItem(STORAGE_KEYS.CURRENT_USER, user);
  }

  setSession(session) {
    return this.setItem(STORAGE_KEYS.SESSION, session);
  }

  // Default settings
  getDefaultSettings() {
    return {
      storeName: 'My Retail Store',
      address: '',
      taxRate: 0,
      currency: 'USD',
      lowStockThreshold: 10,
      logo: ''
    };
  }

  // Password hashing
  hashPassword(password) {
    return CryptoJS.SHA256(password).toString();
  }

  // Export all data
  exportAllData() {
    return {
      users: this.getUsers(),
      products: this.getProducts(),
      categories: this.getCategories(),
      suppliers: this.getSuppliers(),
      transactions: this.getTransactions(),
      stockHistory: this.getStockHistory(),
      settings: this.getSettings(),
      exportDate: new Date().toISOString()
    };
  }

  // Import all data
  importAllData(data) {
    try {
      if (data.users) this.setUsers(data.users);
      if (data.products) this.setProducts(data.products);
      if (data.categories) this.setCategories(data.categories);
      if (data.suppliers) this.setSuppliers(data.suppliers);
      if (data.transactions) this.setTransactions(data.transactions);
      if (data.stockHistory) this.setStockHistory(data.stockHistory);
      if (data.settings) this.setSettings(data.settings);
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  }

  // Initialize with demo data
  initializeDemoData() {
    // Create demo admin user
    const demoUsers = [
      {
        id: 'user-1',
        username: 'admin',
        passwordHash: this.hashPassword('admin123'),
        role: 'user',
        createdAt: new Date().toISOString()
      },
      {
        id: 'user-2',
        username: 'manager',
        passwordHash: this.hashPassword('manager123'),
        role: 'user',
        createdAt: new Date().toISOString()
      }
    ];

    // Demo categories
    const demoCategories = [
      { id: 'cat-1', name: 'Electronics', color: '#002FA7', defaultMarkup: 30 },
      { id: 'cat-2', name: 'Clothing', color: '#FF4F00', defaultMarkup: 50 },
      { id: 'cat-3', name: 'Food & Beverages', color: '#10B981', defaultMarkup: 25 },
      { id: 'cat-4', name: 'Home & Garden', color: '#F59E0B', defaultMarkup: 40 }
    ];

    // Demo suppliers
    const demoSuppliers = [
      {
        id: 'sup-1',
        name: 'Tech Supplies Co',
        contact: 'John Doe',
        phone: '+1-555-0101',
        email: 'john@techsupplies.com',
        address: '123 Tech Street, Silicon Valley, CA 94000',
        notes: 'Primary electronics supplier'
      },
      {
        id: 'sup-2',
        name: 'Fashion Wholesale',
        contact: 'Jane Smith',
        phone: '+1-555-0202',
        email: 'jane@fashionwholesale.com',
        address: '456 Fashion Ave, New York, NY 10001',
        notes: 'Clothing and accessories'
      }
    ];

    // Demo products
    const demoProducts = [
      {
        id: 'prod-1',
        name: 'Wireless Headphones',
        sku: 'WH-001',
        category: 'Electronics',
        description: 'Premium wireless headphones with noise cancellation',
        costPrice: 50,
        sellingPrice: 99.99,
        quantity: 45,
        minStock: 10,
        supplier: 'Tech Supplies Co',
        image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400',
        unit: 'pieces',
        location: 'A1',
        dateAdded: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        lastUpdated: new Date().toISOString(),
        notes: ''
      },
      {
        id: 'prod-2',
        name: 'Smart Watch',
        sku: 'SW-002',
        category: 'Electronics',
        description: 'Fitness tracker and smartwatch',
        costPrice: 80,
        sellingPrice: 149.99,
        quantity: 8,
        minStock: 10,
        supplier: 'Tech Supplies Co',
        image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400',
        unit: 'pieces',
        location: 'A2',
        dateAdded: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
        lastUpdated: new Date().toISOString(),
        notes: 'Low stock alert'
      },
      {
        id: 'prod-3',
        name: 'Cotton T-Shirt',
        sku: 'TS-003',
        category: 'Clothing',
        description: 'Premium cotton t-shirt, various colors',
        costPrice: 10,
        sellingPrice: 24.99,
        quantity: 120,
        minStock: 20,
        supplier: 'Fashion Wholesale',
        image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400',
        unit: 'pieces',
        location: 'B1',
        dateAdded: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
        lastUpdated: new Date().toISOString(),
        notes: ''
      },
      {
        id: 'prod-4',
        name: 'Denim Jeans',
        sku: 'DJ-004',
        category: 'Clothing',
        description: 'Classic blue denim jeans',
        costPrice: 25,
        sellingPrice: 59.99,
        quantity: 0,
        minStock: 15,
        supplier: 'Fashion Wholesale',
        image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=400',
        unit: 'pieces',
        location: 'B2',
        dateAdded: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
        lastUpdated: new Date().toISOString(),
        notes: 'Out of stock - reorder needed'
      },
      {
        id: 'prod-5',
        name: 'Organic Coffee Beans',
        sku: 'CB-005',
        category: 'Food & Beverages',
        description: '1kg premium organic coffee beans',
        costPrice: 15,
        sellingPrice: 29.99,
        quantity: 65,
        minStock: 20,
        supplier: 'Tech Supplies Co',
        image: 'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=400',
        unit: 'kg',
        location: 'C1',
        dateAdded: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        lastUpdated: new Date().toISOString(),
        notes: ''
      }
    ];

    this.setUsers(demoUsers);
    this.setCategories(demoCategories);
    this.setSuppliers(demoSuppliers);
    // Assign ownership of demo products to the first demo user (admin)
    const demoProductsOwned = demoProducts.map(p => ({ ...p, createdBy: 'user-1' }));

    // Add a demo transaction
    const demoTransactions = [
      {
        id: 'txn-demo-1',
        date: new Date().toISOString(),
        products: [
          { productId: demoProductsOwned[0].id, name: demoProductsOwned[0].name, quantity: 2, price: demoProductsOwned[0].sellingPrice }
        ],
        subtotal: demoProductsOwned[0].sellingPrice * 2,
        discount: 0,
        total: demoProductsOwned[0].sellingPrice * 2,
        paymentMethod: 'cash',
        customer: 'Demo Customer',
        user: 'admin',
        userId: 'user-1'
      }
    ];

    this.setProducts(demoProductsOwned);
    this.setTransactions(demoTransactions);
    this.setStockHistory([]);
  }
}

export default new StorageManager();
export { STORAGE_KEYS };